import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  PhotoLibrary,
  Person,
  ArrowBack,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ImageUploader from '../../components/Gallery/ImageUploader';
import galleryService from '../../services/galleryService';
import { trackProviderSectionSave } from '../../services/analyticsService';
import providerService from '../../services/providerService';

const GalleryManager = ({ providerId, embedded = false, onDirtyChange, initialTab = 0 }) => {
  const navigate = useNavigate();
  const { user, isProvider, isAdmin, isAuthenticated } = useAuth();
  // initialTab: a profilszerkesztő teljesség-chipjéről érkezve (1 = Profilkép)
  const [tabValue, setTabValue] = useState(initialTab);
  const [saveStatus, setSaveStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const [galleryImages, setGalleryImages] = useState([]);
  const [profileImage, setProfileImage] = useState([]);

  const actualProviderId = providerId || user?.provider?.id;

  // Debounce-olt auto-mentéshez és a unmount-kori flush-hoz kellő refek
  const debounceRef = useRef(null);
  const latestStateRef = useRef({ isDirty: false, galleryImages: [], profileImage: [] });
  const persistGalleryRef = useRef(null);

  // A callbacket refben tartjuk: ha a szülő minden rendernél új függvényt ad át, az ne
  // indítsa újra az effektet (különben render-ciklus és kérés-áradat lehet a szülőben).
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;

    // Böngésző-szintű elhagyás (bezárás, frissítés, külső URL) — ilyenkor a függő mentés elveszhet.
    // SPA-n belüli navigációnál nem kérdezünk: az unmount-kori flush (lent) elmenti a módosítást.
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  const fetchGalleryData = useCallback(async (silent = false) => {
    if (!actualProviderId) {
      if (!silent) setLoading(false);
      return;
    }

    if (!silent) setLoading(true);
    try {
      const providerResponse = await providerService.getProviderDetails(actualProviderId);
      if (providerResponse.success) {
        setCurrentProvider(providerResponse.data?.provider || providerResponse.data);
      } else {
        console.error('Error fetching provider details:', providerResponse.message);
      }

      const galleryResponse = await galleryService.getGalleryImages(actualProviderId);
      if (galleryResponse.success) {
        setGalleryImages(galleryResponse.data);
      } else {
        console.error('Error fetching gallery images:', galleryResponse.message);
      }

      const profileImageResponse = await galleryService.getProfileImage(actualProviderId);
      if (profileImageResponse.success && profileImageResponse.data) {
        setProfileImage([profileImageResponse.data]);
      } else {
        console.error('Error fetching profile image:', profileImageResponse.message);
        setProfileImage([]);
      }

    } catch (error) {
      console.error('Error fetching gallery data:', error);
      setSaveStatus({ type: 'error', message: 'Hiba történt a galéria adatok betöltése során.' });
    } finally {
      if (!silent) setLoading(false);
      setIsDirty(false);
    }
  }, [actualProviderId]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (!embedded) navigate('/login');
      return;
    }
    if (!isAdmin() && !isProvider()) {
      if (!embedded) navigate('/dashboard');
      return;
    }
    fetchGalleryData();
  }, [isAuthenticated, isAdmin, isProvider, navigate, fetchGalleryData, embedded]);

  // Kiemelve, hogy az auto-mentés (debounce) és a manuális "Újrapróbálkozás" is ugyanazt hívja
  // FONTOS: ez és a hozzá tartozó effektek a korai `return null` ELŐTT kell maradjanak (Rules of Hooks).
  const persistGallery = useCallback(async (imagesToSave, profileImageToSave) => {
    if (!actualProviderId) {
      setSaveStatus({ type: 'error', message: 'Hiányzó szolgáltató ID!' });
      return;
    }

    setSaveStatus({ type: 'loading', message: 'Mentés...' });
    try {
      const galleryUpdateResponse = await galleryService.updateGallery(actualProviderId, imagesToSave);
      if (!galleryUpdateResponse.success) {
        throw new Error(galleryUpdateResponse.message || 'Hiba a galéria képek frissítése során.');
      }

      const profileImageUrl = profileImageToSave.length > 0 ? profileImageToSave[0].url : null;
      const profileImageUpdateResponse = await galleryService.updateProfileImage(actualProviderId, profileImageUrl);
      if (!profileImageUpdateResponse.success) {
        throw new Error(profileImageUpdateResponse.message || 'Hiba a profilkép frissítése során.');
      }

      setSaveStatus({ type: 'success', message: 'Minden módosítás automatikusan mentve.' });
      trackProviderSectionSave(actualProviderId, 'galeria');
      setIsDirty(false);
      // Csendes újratöltés (spinner nélkül), hogy a backend által generált ID-k frissüljenek,
      // különben a következő auto-mentés a temp- ID-jú képeket duplikálva hozná létre.
      await fetchGalleryData(true);
      setTimeout(() => setSaveStatus(null), 2500);

    } catch (error) {
      console.error('Error saving gallery:', error);
      setSaveStatus({ type: 'error', message: error.message || 'Hiba történt a mentés során!' });
      // isDirty szándékosan marad true, hogy a következő módosítás vagy az Újrapróbálkozás gomb újra próbálkozzon
    }
  }, [actualProviderId, fetchGalleryData]);

  useEffect(() => {
    persistGalleryRef.current = persistGallery;
  }, [persistGallery]);

  useEffect(() => {
    latestStateRef.current = { isDirty, galleryImages, profileImage };
  }, [isDirty, galleryImages, profileImage]);

  // Debounce-olt auto-mentés: minden módosítás (feltöltés, törlés, sorrend, kiemelt kép) után
  useEffect(() => {
    if (!isDirty) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      persistGallery(galleryImages, profileImage);
    }, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryImages, profileImage, isDirty]);

  // Unmount-kor: ha még függőben van egy módosítás a debounce-ablakon belül, azonnal mentsük
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const { isDirty: dirtyAtUnmount, galleryImages: imagesAtUnmount, profileImage: profileAtUnmount } = latestStateRef.current;
      if (dirtyAtUnmount) {
        persistGalleryRef.current?.(imagesAtUnmount, profileAtUnmount);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAuthenticated || (!isAdmin() && !isProvider())) {
    return null;
  }

  const handleGalleryImagesChange = (newImages) => {
    setGalleryImages(newImages);
    setIsDirty(true);
  };

  const handleProfileImageChange = (newImages) => {
    if (newImages.length > 0) {
      setProfileImage([{
        ...newImages[0],
        id: 'profile',
        title: 'Profilkép',
        isFeatured: true
      }]);
    } else {
      setProfileImage([]);
    }
    setIsDirty(true);
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Galéria adatok betöltése...</Typography>
      </Box>
    );
  }

  const content = (
    <Box>
      {!embedded && (
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mb: 2 }}
          >
            Vissza a Dashboard-hoz
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <PhotoLibrary color="primary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4">
                Galéria kezelése
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {currentProvider?.businessName || user?.businessName || 'Szolgáltató'} – Képek feltöltése és kezelése
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {saveStatus?.type === 'error' && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => persistGallery(galleryImages, profileImage)}>
              Újrapróbálkozás
            </Button>
          }
        >
          {saveStatus.message}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, minHeight: 32 }}>
        {saveStatus?.type === 'loading' && (
          <>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Mentés...</Typography>
          </>
        )}
        {saveStatus?.type === 'success' && (
          <>
            <CheckCircle fontSize="small" color="success" />
            <Typography variant="body2" color="success.main">{saveStatus.message}</Typography>
          </>
        )}
        {!saveStatus && isDirty && (
          <Typography variant="body2" color="text.secondary">Módosítások mentése hamarosan...</Typography>
        )}
        {!saveStatus && !isDirty && (
          <Typography variant="body2" color="text.secondary">Minden módosítás automatikusan mentésre kerül.</Typography>
        )}
      </Box>

      <Card>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 0, sm: 2 } }}
        >
          <Tab label={`Galéria (${galleryImages.length}/20)`} icon={<PhotoLibrary />} iconPosition="start" />
          <Tab label="Profilkép" icon={<Person />} iconPosition="start" />
        </Tabs>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Munkáim galériája
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Töltse fel a legjobb munkáit, hogy az ügyfelek láthassák a portfólióját.
                Maximum 20 képet tölthet fel. Jelölje ki a kiemelt képet, amely elsőként jelenik meg.
              </Typography>

              <ImageUploader
                images={galleryImages}
                onImagesChange={handleGalleryImagesChange}
                maxImages={20}
                allowFeatured={true}
                allowDelete={true}
                uploadType="providerGallery"
                entityId={actualProviderId}
              />

              {galleryImages.length > 0 && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.primary">
                    <strong>Tipp:</strong> A kiemelt kép (csillaggal jelölve) jelenik meg elsőként a szolgáltatói oldalán.
                    Válassza ki a legjobb munkáját!
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Profilkép beállítása
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Töltse fel a profilképét, amely megjelenik a szolgáltatói kártyáján és a részletes oldalán.
                Ajánlott méret: 300×300 pixel, négyzet alakú.
              </Typography>

              <ImageUploader
                images={profileImage}
                onImagesChange={handleProfileImageChange}
                maxImages={1}
                allowFeatured={false}
                allowDelete={true}
                uploadType="providerProfile"
                entityId={actualProviderId}
              />

              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.50', borderRadius: 2 }}>
                <Typography variant="body2" color="text.primary">
                  <strong>Fontos:</strong> A profilkép segít az ügyfeleknek felismerni Önt.
                  Használjon professzionális, jó minőségű képet.
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  if (embedded) return content;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {content}
    </Container>
  );
};

export default GalleryManager;